import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { normalizeQuickBoxName, type CardInfo } from '@lub0v/santa-utils';
import { Input, PicturePicker, Stack, Typography, showDialog } from '@src/components';
import { ParticipantFace, CARD_PICTURE_NAMES } from '@src/components/faces/ParticipantFace';
import { santaClient } from '@src/state/client';

type Draft = { name: string; picture: string };
function IdentityFields({ draft, error, onChange }: { draft: Draft; error: string | null; onChange: (change: Partial<Draft>) => void }) {
  const { t } = useTranslation();
  const [name, setName] = useState(draft.name);
  const [picture, setPicture] = useState(draft.picture);
  return <Stack gap={5}>
    <Typography as="p" role="body/base">{t('participantIdentity.hint')}</Typography>
    <Input label={t('box.organizer.renameParticipant.label')} value={name} maxLength={64}
      onValueChange={(value) => { onChange({ name: value }); setName(value); }}
      error={name === draft.name ? error ?? undefined : undefined} testId="participant-identity-name" />
    <Stack gap={2}>
      <Typography role="label/field">{t('organizersPanel.pictureLabel')}</Typography>
      <PicturePicker value={picture} pictures={CARD_PICTURE_NAMES}
        renderPicture={(value) => <ParticipantFace picture={value} />}
        onValueChange={(value) => { onChange({ picture: value }); setPicture(value); }}
        testId="participant-identity-picture" />
    </Stack>
  </Stack>;
}

export function useParticipantIdentityEditor() {
  const { t } = useTranslation();
  return ({ card, people, boxKey, onChanged }: {
    card: CardInfo; people: CardInfo[]; boxKey: string; onChanged: () => Promise<void> | void;
  }) => {
    let draft = { name: card.username, picture: card.picture };
    const show = (error: string | null = null) => showDialog({
      title: t('participantIdentity.title'),
      children: <IdentityFields draft={draft} error={error} onChange={(change) => { draft = { ...draft, ...change }; }} />,
      cancel: t('box.cancel'), submit: t('box.save'),
      testId: 'participant-identity-dialog',
      mobileFullScreen: true,
      onSubmit: async () => {
        const username = draft.name.trim();
        if (username.length < 2 || username.length > 64) {
          show(t('participantIdentity.invalidName'));
          throw new Error('Invalid participant name');
        }
        if (people.some((person) => person.id !== card.id &&
          normalizeQuickBoxName(person.username) === normalizeQuickBoxName(username))) {
          show(t('randomizer.problem.duplicate'));
          throw new Error('Duplicate participant name');
        }
        const result = await santaClient.updateOtherCard({
          key: boxKey, id: card.id, username, picture: draft.picture,
        });
        if (!result.ok) {
          show(t(result.error?.message === 'PARTICIPANT_IDENTITY_LOCKED'
            ? 'participantIdentity.locked' : 'newBox.saveFailed'));
          throw new Error('Participant identity update failed');
        }
        await onChanged();
      },
    });
    show();
  };
}
